import { Grid, Box, useTheme, useMediaQuery, Button } from "@material-ui/core";
import { formatTimestamp } from "swyft-react-core";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Quote } from "../../../../api/Quote/Quote";
import { EEventType, eventAnalytic } from "../../../../app/analytics";
import { createCustomerPortalRedirect } from "../../../../api/SwyftApi";
import {
  hasQuoteSubscriptions,
  paymentTypeTitle,
  // quoteBalanceOutstanding,
  quotePaymentsMade,
  solarGrantAmountAvailable
} from "../../../../app/utils";
import { QuoteDetailSlider } from "./QuoteDetailSlider";
import { QuoteDetailTitle } from "./QuoteDetailTitle";
import QuoteDetailPriceOptions from "./QuoteDetailPriceOptions";
import { useStyles } from "../../checkoutSuccessPage.styles";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { fetchFinanceIrelandEstimate, fetchSolarReport } from "../../../../store/QuoteSlice";
import { RootState } from "../../../../app/rootReducer";
import { MAX_FINAL_COST, MIN_FINAL_COST } from "../../../quoteDetailPage/quoteDetail.constants";
import { PriceFormat } from "../../../../components/typography/PriceFormat";

interface Props {
  quote: Quote;
}

export const QuoteDetail = ({ quote }: Props) => {
  const theme = useTheme();
  const classes = useStyles();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const { isMoreThanSm } = useBreakpoint();
  const { solarReport, financeIrelandEstimate } = useSelector((state: RootState) => state.quote);
  const dispatch = useDispatch();

  const isSolar = quote?.type?.includes("Solar");

  useEffect(() => {
    if (isSolar) {
      dispatch(fetchSolarReport({ quoteId: quote?.id, includeBattery: quote.addons?.some((addon) => addon.selected) }));
    }
  }, [dispatch, isSolar, quote]);

  useEffect(() => {
    if (solarReport && quote)
      dispatch(fetchFinanceIrelandEstimate(Number(quote?.price || 0) - solarGrantAmountAvailable(quote, solarReport)));
  }, [dispatch, quote, solarReport]);

  if (!quote) {
    return null;
  }

  const handleViewPortal = async () => {
    eventAnalytic.record(EEventType.StripeCustomerPortal);
    const portalUrl = await createCustomerPortalRedirect(quote.id);

    window.location.href = portalUrl;
  };

  // const outstandingBalance = quoteBalanceOutstanding(quote);
  const paymentsMade = quotePaymentsMade(quote);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        <Box maxWidth={isSmall ? "250px" : "320px"} margin="auto">
          <QuoteDetailSlider lineItems={quote.activeItems} />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Grid container spacing={isSmall ? 1 : 2}>
          <Grid item xs={12}>
            <Box textAlign={isSmall ? "center" : "left"}>
              <Box>
                <QuoteDetailTitle quoteTitle={quote.title} />
              </Box>
              <Box mt={isSmall ? "34px" : "23px"}>
                <Box fontWeight={500} fontSize={isMoreThanSm ? "22px" : "18px"} color="#160037">
                  <PriceFormat value={quote.price} />
                </Box>
              </Box>
              {isSolar ? (
                <>
                  {Number(quote.price) - solarGrantAmountAvailable(quote, solarReport) < MAX_FINAL_COST &&
                  Number(quote.price) - solarGrantAmountAvailable(quote, solarReport) > MIN_FINAL_COST ? (
                    <>
                      <Box
                        fontSize={isMoreThanSm ? "11px" : "9px"}
                        fontWeight={500}
                        mt={isMoreThanSm ? "10px" : "5px"}
                        color="#84868c"
                      >
                        OR{" "}
                        <span style={{ fontSize: isMoreThanSm ? "15px" : "13px" }}>
                          <PriceFormat value={financeIrelandEstimate?.monthlyPayment || 0} />
                        </span>{" "}
                        pm
                      </Box>
                      <img
                        src="images/financeIreland.png"
                        alt="financeIreland"
                        height={isMoreThanSm ? "11px" : "9px"}
                      />
                    </>
                  ) : null}
                </>
              ) : (
                <Box mt={isSmall ? 1 : 0} mb={isSmall ? "13px" : "41px"}>
                  <QuoteDetailPriceOptions quote={quote} textAlign={isSmall ? "center" : "left"} />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            {paymentsMade.length > 0 && (
              <Box>
                {(hasQuoteSubscriptions(quote.subscriptions) || quote.requestedInstallationDate) && (
                  <>
                    <Box fontWeight={400} fontSize={isMoreThanSm ? "16px" : "14px"} color="#65686f">
                      Status
                    </Box>
                  </>
                )}
                {hasQuoteSubscriptions(quote.subscriptions) && (
                  <>
                    <Box height={"1px"} width={"100%"} bgcolor={"#efefef"} my="14px" />
                    {quote.subscriptions!.map((s, i) => (
                      <Box key={i + "subscription"}>
                        <Box display="flex" justifyContent="space-between" my={1}>
                          <Box>
                            <Box fontWeight={400} fontSize={isMoreThanSm ? "14px" : "12px"} color="#4b5462">
                              {s.name}
                            </Box>
                          </Box>
                          <Box
                            display={"flex"}
                            fontWeight={500}
                            fontSize={isMoreThanSm ? "14px" : "12px"}
                            color="#160037"
                          >
                            <Box fontWeight={500} fontSize={isMoreThanSm ? "14px" : "12px"} color="#160037">
                              <PriceFormat value={s.price} />
                            </Box>
                            &nbsp;{paymentTypeTitle(s.paymentType, true)}
                          </Box>
                        </Box>
                        <Box display="flex">
                          <Button
                            onClick={handleViewPortal}
                            className={classes.btnSubscription}
                            size="small"
                            color="secondary"
                          >
                            View Subscription
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </>
                )}
                {quote.requestedInstallationDate && (
                  <>
                    <Box height={"1px"} width={"100%"} bgcolor={"#efefef"} my="14px" />
                    <Box display="flex" justifyContent="space-between" my={1}>
                      <Box fontWeight={400} fontSize={isMoreThanSm ? "14px" : "12px"} color="#4b5462">
                        Installation date
                      </Box>
                      <Box fontWeight={400} fontSize={isMoreThanSm ? "14px" : "12px"} color="#160037">
                        {quote.requestedInstallationDate &&
                          formatTimestamp(new Date(quote.requestedInstallationDate).getTime())}
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
